/* eslint-disable no-param-reassign, react/no-unknown-property */
import { useFrame, useThree } from '@react-three/fiber'
import React from 'react'
import * as THREE from 'three'
import { Reflector } from 'three/examples/jsm/objects/Reflector'

const activeReflectors = new Set<Reflector>()

export interface PlanarReflectorProps {
  node: THREE.Mesh
  resolution?: number
  color?: string
  facingThreshold?: number
  clipBias?: number
  multisample?: number
  flipNormal?: boolean
  updateInterval?: number
  centerCtrl?: [number, number, number] | []
  rotationCtrl?: [number, number, number] | []
  shape?: 'rectangle' | 'rounded' | 'circle'
  cornerRadius?: number
}

export interface PlanarReflectorControl extends PlanarReflectorProps {
  enabled?: boolean
}

function PlanarReflector({
  node,
  resolution = 1024,
  color = '#888888',
  facingThreshold = 0,
  clipBias = 0.02,
  multisample = 4,
  flipNormal = false,
  updateInterval = 1,
  centerCtrl = [],
  rotationCtrl = [],
  shape = 'rectangle',
  cornerRadius = 0.1,
}: PlanarReflectorProps) {
  const gl = useThree((state) => state.gl)
  const camera = useThree((state) => state.camera)

  const frameRef = React.useRef(0)
  const renderedOnceRef = React.useRef(false)
  const updateIntervalRef = React.useRef(updateInterval)

  React.useEffect(() => {
    updateIntervalRef.current = updateInterval
  }, [updateInterval])

  const { reflector, localCenter, orientation } = React.useMemo(() => {
    const { geometry } = node
    geometry.computeBoundingBox()
    const bb = geometry.boundingBox ?? new THREE.Box3()

    const baseCenter = new THREE.Vector3()
    bb.getCenter(baseCenter)

    const size = new THREE.Vector3()
    bb.getSize(size)

    const axes = [
      { dir: new THREE.Vector3(1, 0, 0), len: size.x },
      { dir: new THREE.Vector3(0, 1, 0), len: size.y },
      { dir: new THREE.Vector3(0, 0, 1), len: size.z },
    ].sort((a, b) => a.len - b.len)

    const normalAxis = axes[0].dir.clone()
    const faceNormal = new THREE.Vector3(0, 0, 1)
    const normalAttr = geometry.attributes.normal
    if (normalAttr) {
      faceNormal
        .fromBufferAttribute(normalAttr as THREE.BufferAttribute, 0)
        .normalize()
    }
    if (normalAxis.dot(faceNormal) < 0) normalAxis.negate()
    if (flipNormal) normalAxis.negate()

    const widthAxis = axes[2].dir.clone()
    const heightAxis = new THREE.Vector3()
      .crossVectors(normalAxis, widthAxis)
      .normalize()

    widthAxis.crossVectors(heightAxis, normalAxis).normalize()

    const baseOrientation = new THREE.Quaternion().setFromRotationMatrix(
      new THREE.Matrix4().makeBasis(widthAxis, heightAxis, normalAxis)
    )

    const w = axes[2].len
    const h = axes[1].len

    let planeGeometry: THREE.BufferGeometry
    if (shape === 'circle') {
      planeGeometry = new THREE.CircleGeometry(Math.min(w, h) / 2, 64)
    } else if (shape === 'rounded') {
      const r = Math.min(cornerRadius, w / 1.5, h / 1.5)
      const s = new THREE.Shape()
      const x = -w / 2
      const y = -h / 2
      s.moveTo(x + r, y)
      s.lineTo(x + w - r, y)
      s.quadraticCurveTo(x + w, y, x + w, y + r)
      s.lineTo(x + w, y + h - r)
      s.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
      s.lineTo(x + r, y + h)
      s.quadraticCurveTo(x, y + h, x, y + h - r)
      s.lineTo(x, y + r)
      s.quadraticCurveTo(x, y, x + r, y)
      planeGeometry = new THREE.ShapeGeometry(s)
    } else {
      planeGeometry = new THREE.PlaneGeometry(w, h)
    }

    const dpr = gl.getPixelRatio()

    const nextReflector = new Reflector(planeGeometry, {
      color: new THREE.Color(color),
      textureWidth: resolution * dpr,
      textureHeight: resolution * dpr,
      clipBias,
      multisample,
    })

    return {
      reflector: nextReflector,
      localCenter: baseCenter,
      orientation: baseOrientation,
    }
  }, [
    node,
    resolution,
    color,
    gl,
    clipBias,
    multisample,
    flipNormal,
    shape,
    cornerRadius,
  ])

  const center = React.useMemo<[number, number, number]>(() => {
    const [ctrlX, ctrlY, ctrlZ] = centerCtrl
    return [
      localCenter.x + (ctrlX || 0),
      localCenter.y + (ctrlY || 0),
      localCenter.z + (ctrlZ || 0),
    ]
  }, [localCenter, centerCtrl])

  const quaternion = React.useMemo<[number, number, number, number]>(() => {
    const [pitch, yaw, roll] = rotationCtrl

    const delta = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(
        THREE.MathUtils.degToRad(pitch || 0),
        THREE.MathUtils.degToRad(yaw || 0),
        THREE.MathUtils.degToRad(roll || 0),
        'YXZ'
      )
    )
    const quat = orientation.clone().multiply(delta)
    return [quat.x, quat.y, quat.z, quat.w]
  }, [orientation, rotationCtrl])

  React.useEffect(() => {
    activeReflectors.add(reflector)
    frameRef.current = 0
    renderedOnceRef.current = false

    const baseBeforeRender = reflector.onBeforeRender
    const baseAfterRender = reflector.onAfterRender
    let hiddenDuringRender: Reflector[] = []

    reflector.onBeforeRender = function wrappedBeforeRender(...args) {
      const interval = updateIntervalRef.current
      if (interval <= 0) {
        if (renderedOnceRef.current) return
        renderedOnceRef.current = true
      } else {
        const frame = frameRef.current
        frameRef.current += 1
        if (frame % interval !== 0) return
      }

      hiddenDuringRender = []
      activeReflectors.forEach((other) => {
        if (other !== reflector && other.visible) {
          other.visible = false
          hiddenDuringRender.push(other)
        }
      })
      baseBeforeRender.apply(this, args)
    }

    reflector.onAfterRender = function wrappedAfterRender(...args) {
      baseAfterRender.apply(this, args)
      hiddenDuringRender.forEach((other) => {
        other.visible = true
      })
      hiddenDuringRender = []
    }

    return () => {
      activeReflectors.delete(reflector)
      reflector.getRenderTarget().dispose()
      reflector.dispose()
      reflector.geometry.dispose()
    }
  }, [reflector])

  const temp = React.useMemo(
    () => ({
      normalMatrix: new THREE.Matrix3(),
      normal: new THREE.Vector3(),
      worldPosition: new THREE.Vector3(),
      toCamera: new THREE.Vector3(),
    }),
    []
  )

  useFrame(() => {
    reflector.getWorldPosition(temp.worldPosition)
    temp.normalMatrix.getNormalMatrix(reflector.matrixWorld)
    temp.normal.set(0, 0, 1).applyMatrix3(temp.normalMatrix).normalize()
    const frontDistance = temp.toCamera
      .subVectors(camera.position, temp.worldPosition)
      .dot(temp.normal)
    reflector.visible = frontDistance > facingThreshold
  })

  return (
    <group position={node.position} rotation={node.rotation} scale={node.scale}>
      <primitive object={reflector} position={center} quaternion={quaternion} />
    </group>
  )
}

export default PlanarReflector
